import type { SupabaseClient } from '@supabase/supabase-js'
import * as surfaceAppsModule from '@shell-surface-apps'
import { activeInstance } from '../instances'
import type { MailApiGet } from './mail-api'

// Module gating (WP4): a surface not in this instance's modules contributes
// no palette results (its provider module is never even imported).
const moduleEnabled = (id: string): boolean =>
  (activeInstance.modules as readonly string[]).includes(id)

/**
 * The shell's normalized palette result. Three surfaces expose search
 * providers and each declared its own local SearchResult shape (crm, mail,
 * projects — none share a type; @omni/objects doesn't own one yet), so the
 * shell adapts all three here. Hrefs come back shell-absolute ('/crm/…').
 */
export interface PaletteResult {
  key: string
  app: string
  appLabel: string
  title: string
  subtitle?: string
  href: string
}

// An instance can bring one search of its own across all its apps (`searchProvider` in its surface-apps
// module; Nateva: the database search over people, blocks, deals, agreements, notes and more). Same rows as the
// front page search box. Instances without one are unchanged.
const instanceSearch = (surfaceAppsModule as Record<string, unknown>).searchProvider as
  | ((query: string, signal: AbortSignal, client: unknown) => Promise<PaletteResult[]>)
  | undefined

// Providers are imported lazily (and directly from their module, not through
// federation-entry) so opening the palette doesn't pull any surface's App
// chunk. Failures are per-provider: a failing surface just contributes
// nothing. crm + projects providers are factories that take the shell's
// shared tenant client (single-DB posture) — they no longer build their own.
export async function runSearch(
  query: string,
  signal: AbortSignal,
  mailApiGet: MailApiGet | null,
  tenantClient: SupabaseClient,
): Promise<PaletteResult[]> {
  const tasks: Array<Promise<PaletteResult[]>> = []

  if (instanceSearch) tasks.push(instanceSearch(query, signal, tenantClient))

  if (moduleEnabled('crm')) {
    tasks.push(
      (async () => {
        const { makeSearchProvider } = await import(
          '@omni-command/crm/src/federation/searchProvider'
        )
        const results = await makeSearchProvider(tenantClient)(query, signal)
        return results.map((r) => ({
          key: `crm:${r.kind}:${r.id}`,
          app: 'crm',
          appLabel: 'CRM',
          title: r.title,
          subtitle: r.subtitle,
          href: r.href,
        }))
      })(),
    )
  }

  if (moduleEnabled('projects')) {
    tasks.push(
      (async () => {
        const { makeSearchProvider } = await import(
          '@omni-command/projects/src/federation/searchProvider'
        )
        const results = await makeSearchProvider(tenantClient)(query, signal)
        return results.map((r) => ({
          key: `projects:${r.kind}:${r.id}`,
          app: 'projects',
          appLabel: 'Projects',
          title: r.title,
          subtitle: r.subtitle,
          href: r.href,
        }))
      })(),
    )
  }

  if (mailApiGet && moduleEnabled('mail')) {
    tasks.push(
      (async () => {
        const { makeSearchProvider } = await import(
          '@omni-command/mail/src/federation/searchProvider'
        )
        const results = await makeSearchProvider({ apiGet: mailApiGet })(query)
        return results.map((r) => ({
          key: `mail:${r.kind}:${r.id}`,
          app: 'mail',
          appLabel: 'Mail',
          title: r.title,
          subtitle: r.subtitle,
          href: r.href,
        }))
      })(),
    )
  }

  const settled = await Promise.allSettled(tasks)
  if (signal.aborted) return []
  const out: PaletteResult[] = []
  for (const s of settled) {
    if (s.status === 'fulfilled') out.push(...s.value)
    else if (import.meta.env.DEV) console.warn('[palette] provider failed', s.reason)
  }
  return out
}
